/**
 * Sentry Configuration for Lumi Web Application
 *
 * Provides comprehensive error tracking, performance monitoring,
 * and user experience insights with privacy compliance.
 */

import * as Sentry from "@sentry/react";
import React from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

/**
 * Initialize Sentry with React and React Router integration
 */
export function initSentry() {
  // Skip initialization in development unless explicitly enabled
  if (import.meta.env.MODE === "development" && !import.meta.env.VITE_SENTRY_ENABLED) {
    console.log("Sentry disabled in development mode");
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN as string;
  if (!dsn) {
    console.warn("Sentry DSN not configured");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // Performance monitoring
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1,

    // React Router v6 integration for navigation tracking
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // User interaction replay for debugging
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text content
        blockAllMedia: true, // Privacy: block all media
      }),
    ],

    // Data scrubbing for privacy compliance
    beforeSend(event, hint) {
      return scrubSensitiveData(event, hint);
    },

    // Release tracking
    release: (import.meta.env.VITE_APP_VERSION as string) || "unknown",

    // Initial user context
    initialScope: {
      tags: {
        component: "web-app",
      },
    },
  });

  console.log(`Sentry initialized in ${import.meta.env.MODE} mode`);
}

/**
 * Scrub sensitive data from error reports for privacy compliance
 */
function scrubSensitiveData(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): Sentry.ErrorEvent | null {
  // Remove sensitive headers
  scrubHeaders(event);

  // Remove sensitive context data
  scrubExtraData(event);
  scrubTagData(event);

  // Filter out low-value errors
  return shouldSkipError(hint) ? null : event;
}

function scrubHeaders(event: Sentry.ErrorEvent): void {
  if (event.request?.headers) {
    delete event.request.headers.Authorization;
    delete event.request.headers.Cookie;
    delete event.request.headers["X-API-Key"];
  }
}

function scrubExtraData(event: Sentry.ErrorEvent): void {
  if (!event.extra) return;

  const sensitiveKeys = ["password", "token", "secret", "key"];
  for (const key of Object.keys(event.extra)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      event.extra[key] = "[Filtered]";
    }
  }
}

function scrubTagData(event: Sentry.ErrorEvent): void {
  if (!event.tags) return;

  const sensitiveKeys = ["email", "user_id", "session"];
  for (const key of Object.keys(event.tags)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      delete event.tags[key];
    }
  }
}

function shouldSkipError(hint?: Sentry.EventHint): boolean {
  const error = hint?.originalException;
  if (!(error instanceof Error)) return false;

  const skipPatterns = ["NetworkError", "Failed to fetch", "ResizeObserver"];
  return skipPatterns.some((pattern) => error.message.includes(pattern));
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    // Only include email if user consents to analytics
    email: user.email && shouldIncludeUserEmail() ? user.email : undefined,
    role: user.role,
  });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add playback context for video-related errors
 */
export function setSentryPlaybackContext(context: {
  assetId?: string;
  position?: number;
  quality?: string;
  duration?: number;
}) {
  Sentry.setContext("playback", {
    asset_id: context.assetId,
    position: context.position,
    quality: context.quality,
    duration: context.duration,
    timestamp: Date.now(),
  });
}

/**
 * Add navigation breadcrumb
 */
export function addNavigationBreadcrumb(route: string, title?: string) {
  Sentry.addBreadcrumb({
    message: "Navigation",
    level: "info",
    category: "navigation",
    data: {
      route,
      title,
      timestamp: Date.now(),
    },
  });
}

/**
 * Add user action breadcrumb
 */
export function addUserActionBreadcrumb(action: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    level: "info",
    category: "user",
    data: {
      action,
      ...data,
      timestamp: Date.now(),
    },
  });
}

/**
 * Capture performance measurement
 */
export function capturePerformanceMetric(name: string, value: number, unit: string = "ms") {
  Sentry.metrics.distribution(name, value, {
    unit,
  });
}

/**
 * Check if user email should be included (privacy compliance)
 */
function shouldIncludeUserEmail(): boolean {
  // Check if user has consented to analytics
  const consent = localStorage.getItem("lumi_analytics_consent");
  return consent === "true";
}

/**
 * Manual error capture with context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("error_context", context);
    }
    Sentry.captureException(error);
  });
}

/**
 * Export all Sentry functions for direct usage if needed
 */
export * as SentryReact from "@sentry/react";
